// web-frontend/src/shared/services/analyticsService.js
// 🔥 자체 Analytics 서비스
// - 페이지 조회 + 체류 시간 자동 추적
// - 기능 사용(클릭, 모달 등) 수동 추적
// - 이벤트 배치 전송 (10초 간격 또는 10개 이상 쌓이면 즉시)
// - 페이지 이탈 시 sendBeacon으로 체류 시간 전송
// - 세션 ID 자동 관리

import apiClient from './apiClient';
import logger from '../utils/logger';

// ==================== 상수 ====================
const BATCH_INTERVAL_MS = 10000;      // 배치 전송 간격 (10초)
const BATCH_MAX_SIZE = 10;            // 즉시 전송 트리거 개수
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 세션 타임아웃 (30분)
const API_BASE = '/analytics';

// ==================== 유틸리티 ====================

/** 간단한 UUID v4 생성 */
function generateId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** localStorage에서 값 가져오기 (안전하게) */
function getStorage(key, fallback = null) {
  try {
    const val = localStorage.getItem(key);
    return val ? JSON.parse(val) : fallback;
  } catch {
    return fallback;
  }
}

/** localStorage에 값 저장 (안전하게) */
function setStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage 사용 불가 시 무시
  }
}

/** 페이지 경로에서 제목 추론 */
function inferPageTitle(path) {
  const titleMap = {
    '/': '홈',
    '/companies': '기업목록',
    '/chats': '채팅목록',
    '/events': '일정',
    '/matching': '매칭',
    '/meetings': '미팅',
    '/profile': '프로필',
    '/map': '부스맵',
    '/carbon': '탄소활동',
    '/login': '로그인',
    '/register': '회원가입',
    '/survey': '설문조사',
    '/survey-intro': '설문안내',
  };

  // 정확한 매칭 먼저
  if (titleMap[path]) return titleMap[path];

  // 프리픽스 매칭
  for (const [prefix, title] of Object.entries(titleMap)) {
    if (path.startsWith(prefix + '/')) return title + ' 상세';
  }

  return path;
}

// ==================== Analytics 서비스 클래스 ====================

class AnalyticsService {
  constructor() {
    // 이벤트 큐 (배치 전송용)
    this._queue = [];
    this._batchTimerId = null;

    // 세션 관리
    this._sessionId = null;
    this._deviceId = null;

    // 페이지 추적 상태
    this._currentPage = null;
    this._pageEnterTime = null;
    this._referrerPath = null;

    // 플래그
    this._initialized = false;
    this._enabled = true; // 운영 환경 toggle

    // 디바이스 정보 캐시
    this._screenWidth = typeof window !== 'undefined' ? window.innerWidth : null;
    this._screenHeight = typeof window !== 'undefined' ? window.innerHeight : null;
    this._platform = this._detectPlatform();
    this._language = null;
  }

  // ==========================================
  // 🚀 초기화
  // ==========================================

  /**
   * Analytics 서비스 초기화
   * App.jsx에서 앱 시작 시 호출합니다.
   */
  initialize({ language = 'ko', enabled = true } = {}) {
    if (this._initialized) return;

    this._enabled = enabled;
    this._language = language;

    if (!this._enabled) {
      logger.debug('📊 [Analytics] 비활성화 상태');
      return;
    }

    // 세션/디바이스 ID 복구 또는 생성
    this._initSession();
    this._initDeviceId();

    // 배치 전송 타이머 시작
    this._startBatchTimer();

    // 페이지 이탈 시 체류 시간 전송 (beforeunload + visibilitychange)
    this._registerUnloadHandlers();

    // 윈도우 리사이즈 감지
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => {
        this._screenWidth = window.innerWidth;
        this._screenHeight = window.innerHeight;
      });
    }

    this._initialized = true;
    logger.debug('📊 [Analytics] 초기화 완료', {
      sessionId: this._sessionId?.substring(0, 8) + '...',
      deviceId: this._deviceId?.substring(0, 8) + '...',
    });
  }

  /** 언어 설정 업데이트 */
  setLanguage(language) {
    this._language = language;
  }

  /** 활성화/비활성화 토글 */
  setEnabled(enabled) {
    this._enabled = enabled;
    if (!enabled) {
      this._stopBatchTimer();
      this._queue = [];
    } else if (!this._batchTimerId) {
      this._startBatchTimer();
    }
  }

  // ==========================================
  // 📄 페이지 추적 (자동)
  // ==========================================

  /**
   * 페이지 진입 기록
   * 라우트 변경 시 자동으로 호출됩니다.
   *
   * @param {string} path - 현재 페이지 경로
   * @param {string} [title] - 페이지 제목 (없으면 자동 추론)
   */
  trackPageView(path, title) {
    if (!this._enabled) return;

    // 이전 페이지 체류 시간 기록
    this._recordPageLeave();

    // 새 페이지 진입 기록
    const pageTitle = title || inferPageTitle(path);
    this._referrerPath = this._currentPage;
    this._currentPage = path;
    this._pageEnterTime = Date.now();

    this._pushEvent({
      event_type: 'page_view',
      event_name: 'page_enter:' + path,
      page_path: path,
      page_title: pageTitle,
      referrer_path: this._referrerPath,
    });
  }

  /**
   * 페이지 이탈 기록 (내부용)
   * 다른 페이지로 이동하거나 브라우저를 닫을 때 호출됩니다.
   */
  _recordPageLeave() {
    if (!this._currentPage || !this._pageEnterTime) return;

    const durationMs = Date.now() - this._pageEnterTime;

    // 1초 미만은 무시 (빠른 리다이렉트)
    if (durationMs < 1000) return;

    // 30분 초과는 비활성으로 간주하여 캡핑
    const cappedDuration = Math.min(durationMs, 30 * 60 * 1000);

    this._pushEvent({
      event_type: 'page_view',
      event_name: 'page_leave:' + this._currentPage,
      page_path: this._currentPage,
      referrer_path: this._referrerPath,
      duration_ms: cappedDuration,
    });
  }

  // ==========================================
  // 🔘 기능 사용 추적 (수동)
  // ==========================================

  /**
   * 기능 사용 이벤트 기록
   *
   * @param {string} category - 기능 카테고리 (예: 'matching', 'chat', 'company', 'meeting')
   * @param {string} action - 액션 (예: 'click', 'submit', 'open', 'close', 'toggle')
   * @param {string} [label] - 라벨 (예: 'start_matching_btn', 'send_message', 'filter_industry')
   * @param {number} [value] - 숫자 값 (필요 시)
   * @param {object} [metadata] - 추가 메타데이터
   *
   * @example
   * analyticsService.trackFeature('matching', 'click', 'start_matching_btn');
   * analyticsService.trackFeature('chat', 'submit', 'send_message');
   * analyticsService.trackFeature('company', 'click', 'bookmark_company', null, { companyId: '123' });
   * analyticsService.trackFeature('survey', 'submit', 'complete_onboarding');
   */
  trackFeature(category, action, label, value, metadata) {
    if (!this._enabled) return;

    this._pushEvent({
      event_type: 'feature_use',
      event_name: category + ':' + action + (label ? ':' + label : ''),
      page_path: this._currentPage,
      feature_category: category,
      feature_action: action,
      feature_label: label || null,
      feature_value: value != null ? value : null,
      metadata: metadata || null,
    });
  }

  /**
   * 네비게이션 이벤트 기록
   *
   * @param {string} from - 출발 경로
   * @param {string} to - 도착 경로
   * @param {string} [method] - 이동 방법 (예: 'bottom_nav', 'sidebar', 'link', 'back')
   */
  trackNavigation(from, to, method) {
    if (!this._enabled) return;

    this._pushEvent({
      event_type: 'navigation',
      event_name: 'navigate:' + (method || 'unknown'),
      page_path: to,
      referrer_path: from,
      metadata: { method },
    });
  }

  /**
   * 검색 이벤트 기록
   *
   * @param {string} query - 검색어
   * @param {string} [context] - 검색 컨텍스트 (예: 'company_list', 'booth_map')
   * @param {number} [resultCount] - 검색 결과 수
   */
  trackSearch(query, context, resultCount) {
    if (!this._enabled) return;

    this._pushEvent({
      event_type: 'search',
      event_name: 'search:' + (context || 'global'),
      page_path: this._currentPage,
      feature_category: 'search',
      feature_action: 'query',
      feature_label: context || 'global',
      feature_value: resultCount != null ? resultCount : null,
      metadata: { query: query?.substring(0, 100) }, // 검색어 100자 제한
    });
  }

  // ==========================================
  // 📦 이벤트 큐 관리 & 배치 전송
  // ==========================================

  /** 이벤트를 큐에 추가 */
  _pushEvent(eventData) {
    const event = {
      ...eventData,
      timestamp: new Date().toISOString(),
    };

    this._queue.push(event);

    // 큐가 일정 크기 이상이면 즉시 전송
    if (this._queue.length >= BATCH_MAX_SIZE) {
      this._flushQueue();
    }
  }

  /** 큐에 쌓인 이벤트를 서버로 전송 */
  async _flushQueue() {
    if (this._queue.length === 0) return;

    const eventsToSend = [...this._queue];
    this._queue = [];

    const payload = {
      events: eventsToSend,
      session_id: this._sessionId,
      device_id: this._deviceId,
      screen_width: this._screenWidth,
      screen_height: this._screenHeight,
      platform: this._platform,
      language: this._language,
    };

    try {
      await apiClient.post(API_BASE + '/events', payload);
    } catch (error) {
      // 전송 실패 시 큐에 다시 넣기 (최대 50개까지만 유지)
      const merged = [...eventsToSend, ...this._queue];
      this._queue = merged.slice(-50);
      logger.warn('📊 [Analytics] 이벤트 전송 실패, 재시도 예정:', error.message);
    }
  }

  /** 배치 전송 타이머 시작 */
  _startBatchTimer() {
    this._stopBatchTimer();
    this._batchTimerId = setInterval(() => {
      this._flushQueue();
    }, BATCH_INTERVAL_MS);
  }

  /** 배치 전송 타이머 중지 */
  _stopBatchTimer() {
    if (this._batchTimerId) {
      clearInterval(this._batchTimerId);
      this._batchTimerId = null;
    }
  }

  // ==========================================
  // 🔐 세션 & 디바이스 관리
  // ==========================================

  _initSession() {
    const stored = getStorage('analytics_session');

    if (stored && stored.id && stored.lastActive) {
      const elapsed = Date.now() - stored.lastActive;
      if (elapsed < SESSION_TIMEOUT_MS) {
        this._sessionId = stored.id;
        this._touchSession();
        return;
      }
    }

    // 새 세션 생성
    this._sessionId = generateId();
    this._touchSession();
  }

  _touchSession() {
    setStorage('analytics_session', {
      id: this._sessionId,
      lastActive: Date.now(),
    });
  }

  _initDeviceId() {
    let deviceId = getStorage('analytics_device_id');
    if (!deviceId) {
      deviceId = generateId();
      setStorage('analytics_device_id', deviceId);
    }
    this._deviceId = deviceId;
  }

  // ==========================================
  // 🚪 페이지 이탈 핸들러
  // ==========================================

  _registerUnloadHandlers() {
    if (typeof window === 'undefined') return;

    // beforeunload: 브라우저 닫기/새로고침
    window.addEventListener('beforeunload', () => {
      this._sendBeaconOnLeave();
    });

    // visibilitychange: 탭 전환 (모바일에서 중요)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this._sendBeaconOnLeave();
      }
    });

    // pagehide: iOS Safari에서 beforeunload 대안
    window.addEventListener('pagehide', () => {
      this._sendBeaconOnLeave();
    });
  }

  /** sendBeacon으로 페이지 이탈 데이터 전송 */
  _sendBeaconOnLeave() {
    if (!this._currentPage || !this._pageEnterTime) return;

    const durationMs = Date.now() - this._pageEnterTime;
    if (durationMs < 1000) return;

    const cappedDuration = Math.min(durationMs, 30 * 60 * 1000);

    // sendBeacon은 페이지 언로드 중에도 안정적으로 전송 가능
    const payload = JSON.stringify({
      page_path: this._currentPage,
      page_title: inferPageTitle(this._currentPage),
      duration_ms: cappedDuration,
      referrer_path: this._referrerPath,
      session_id: this._sessionId,
      device_id: this._deviceId,
    });

    try {
      // apiClient의 baseURL 가져오기
      // apiClient의 baseURL에 이미 /api가 포함되어 있으므로 직접 조합
      const baseUrl = window.location.origin;
      const url = baseUrl + '/api' + API_BASE + '/page-leave';

      if (navigator.sendBeacon) {
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } else {
        // sendBeacon 미지원 시 동기 XMLHttpRequest (fallback)
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, false); // 동기 호출
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.send(payload);
      }
    } catch (error) {
      // 이탈 시 에러는 무시 (더 이상 처리할 수 없음)
    }

    // 상태 리셋 (중복 전송 방지)
    this._pageEnterTime = null;
  }

  // ==========================================
  // 🛠️ 유틸리티
  // ==========================================

  _detectPlatform() {
    if (typeof window === 'undefined') return 'unknown';
    const ua = navigator.userAgent || '';
    if (/Mobi|Android/i.test(ua)) return 'mobile-web';
    return 'web';
  }

  /** 큐 강제 비우기 (로그아웃 등) */
  flush() {
    this._recordPageLeave();
    this._flushQueue();
  }

  /** 서비스 해제 */
  destroy() {
    this._stopBatchTimer();
    this.flush();
    this._initialized = false;
  }

  /** 현재 세션 ID 반환 */
  getSessionId() {
    return this._sessionId;
  }

  /** 현재 디바이스 ID 반환 */
  getDeviceId() {
    return this._deviceId;
  }
}

// ==================== 싱글톤 인스턴스 ====================
const analyticsService = new AnalyticsService();
export default analyticsService;